import type { QuotationResponse } from '../../api/models/bff/Quotation';
import type { Trip } from '../../api/models/bff/Trip';
import type { PaginationQueryParams } from '../../api/models/bff/Common';
import type { PaymentStatus } from '../../api/models/bff/Payment';
import { generateId, generateReference, generateDate, generateDateWithDayOffset } from '../../mocks/utils/generators';
import {
  type TripSearchResponse,
  type TripSearchParametersResponse,
  type AvailabilityGroupsResponse,
  type TripResult,
  type Availability,
  type AvailabilitySearchResponse,
  ANYWHERE_GROUP_CODE,
} from '../../api/models/bff/TripSearch';
import type {
  BookingsListResponse,
  BookingResponse,
  BookingStatus,
} from '../../api/models/bff/Booking';
import type { SelectedAncillaryPassenger } from '../../api/models/bff/Ancillaries';
import type { FlightOption, PassengerResponse, PassengersListResponse, PassengerType, PassengerEvaluationResponse } from '../../api/models/bff/Passenger';
import { createMockPricing, createMockPricingWithCredits } from './pricing';
import { toSelectedAncillaries, MOCK_ANCILLARIES_INCLUDED, MOCK_ANCILLARIES_PURCHASED } from './ancillaries';

const FRA_LAYOVER_DURATION = 90;
/**
 * Number of destinations (journeys) shown per day for ANYWHERE availability.
 * Change this single value — e.g. to 5 — to reduce the number of airports per date.
 * Must be ≤ ANYWHERE_DESTINATIONS.length (currently 16).
 */
export const ANYWHERE_TRIPS_PER_DATE = 7;
/** Number of active city groups returned by the mock. Change this single value to add or remove routes. */
export const CITY_GROUP_COUNT = 8;

const CONNECTING_SEGMENTS = {
  outbound: [
    {
      departure: { station: { code: 'MAD', type: 'AIRPORT' }, date: { utc: '2026-07-15T09:00:00Z', zoneId: 'Europe/Madrid' } },
      arrival: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-15T12:00:00Z', zoneId: 'Europe/Berlin' } },
      duration: 180,
      serviceNumber: 'LH1806',
      carrier: { code: 'LH', name: 'Lufthansa' },
    },
    {
      departure: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-15T13:30:00Z', zoneId: 'Europe/Berlin' } },
      arrival: { station: { code: 'BCN', type: 'AIRPORT' }, date: { utc: '2026-07-15T15:30:00Z', zoneId: 'Europe/Madrid' } },
      duration: 120,
      serviceNumber: 'LH1174',
      carrier: { code: 'LH', name: 'Lufthansa' },
    },
  ],
  inbound: [
    {
      departure: { station: { code: 'BCN', type: 'AIRPORT' }, date: { utc: '2026-07-22T16:00:00Z', zoneId: 'Europe/Madrid' } },
      arrival: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-22T18:30:00Z', zoneId: 'Europe/Berlin' } },
      duration: 150,
      serviceNumber: 'LH1175',
      carrier: { code: 'LH', name: 'Lufthansa' },
    },
    {
      departure: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-22T20:00:00Z', zoneId: 'Europe/Berlin' } },
      arrival: { station: { code: 'MAD', type: 'AIRPORT' }, date: { utc: '2026-07-22T21:30:00Z', zoneId: 'Europe/Madrid' } },
      duration: 150,
      serviceNumber: 'LH1807',
      carrier: { code: 'LH', name: 'Lufthansa' },
    },
  ],
} as const;

/** BCN → FRA → AMS → MAD — 2 stops, 3 segments */
const INBOUND_2STOP_SEGMENTS = [
  {
    departure: { station: { code: 'BCN', type: 'AIRPORT' }, date: { utc: '2026-07-22T09:00:00Z', zoneId: 'Europe/Madrid' } },
    arrival: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-22T11:30:00Z', zoneId: 'Europe/Berlin' } },
    duration: 150,
    serviceNumber: 'LH1175',
    carrier: { code: 'LH', name: 'Lufthansa' },
  },
  {
    departure: { station: { code: 'FRA', type: 'AIRPORT' }, date: { utc: '2026-07-22T13:00:00Z', zoneId: 'Europe/Berlin' } },
    arrival: { station: { code: 'AMS', type: 'AIRPORT' }, date: { utc: '2026-07-22T14:10:00Z', zoneId: 'Europe/Amsterdam' } },
    duration: 70,
    serviceNumber: 'KL1807',
    carrier: { code: 'KL', name: 'KLM' },
  },
  {
    departure: { station: { code: 'AMS', type: 'AIRPORT' }, date: { utc: '2026-07-22T16:00:00Z', zoneId: 'Europe/Amsterdam' } },
    arrival: { station: { code: 'MAD', type: 'AIRPORT' }, date: { utc: '2026-07-22T18:40:00Z', zoneId: 'Europe/Madrid' } },
    duration: 160,
    serviceNumber: 'KL1706',
    carrier: { code: 'KL', name: 'KLM' },
  },
] as const;

const INBOUND_2STOP_LAST_INDEX = INBOUND_2STOP_SEGMENTS.length - 1;

/**
 * Create mock trip
 *
 * @param overrides - Partial overrides for the trip
 * @returns Mock trip
 */
export function createMockTrip(overrides?: Partial<Trip>): Trip {
  const base = {
    departure: {
      station: { code: 'MAD', type: 'AIRPORT' },
      date: { utc: '2026-07-15T10:00:00Z', zoneId: 'Europe/Madrid' },
    },
    arrival: {
      station: { code: 'BCN', type: 'AIRPORT' },
      date: { utc: '2026-07-15T11:30:00Z', zoneId: 'Europe/Madrid' },
    },
    duration: 90,
    carrier: { code: 'VY', name: 'Vueling' },
    serviceNumber: 'VY1004',
    fare: {
      fareClass: 'ECONOMY' as const,
      family: 'BASIC',
    },
  };

  const merged = { ...base, ...overrides };

  return {
    ...merged,
    segments: overrides?.segments ?? [
      {
        departure: merged.departure,
        arrival: merged.arrival,
        duration: merged.duration,
        serviceNumber: merged.serviceNumber,
        carrier: merged.carrier,
      },
    ],
  };
}

/**
 * Create mock connecting trip — a trip with two segments via an intermediate airport.
 *
 * @param overrides - Partial overrides for the trip
 * @returns Mock connecting trip
 */
export function createMockConnectingTrip(direction: 'outbound' | 'inbound' = 'outbound', overrides?: Partial<Trip>): Trip {
  const [segment1, segment2] = CONNECTING_SEGMENTS[direction];

  return {
    departure: segment1.departure,
    arrival: segment2.arrival,
    duration: segment1.duration + FRA_LAYOVER_DURATION + segment2.duration,
    carrier: { code: 'LH', name: 'Lufthansa' },
    serviceNumber: segment1.serviceNumber,
    fare: {
      fareClass: 'ECONOMY',
      family: 'BASIC',
    },
    segments: [segment1, segment2],
    stops: 0,
    connections: 1,
    ...overrides,
  };
}

/**
 * Create mock trip result for search
 *
 * @param overrides - Partial overrides for the trip result
 * @returns Mock trip result
 */
export function createMockTripResult(overrides?: Partial<TripResult>): TripResult {
  const base = {
    departure: {
      station: { code: 'MAD', type: 'AIRPORT' },
      date: { utc: '2026-07-15T10:00:00Z', zoneId: 'Europe/Madrid' },
    },
    arrival: {
      station: { code: 'BCN', type: 'AIRPORT' },
      date: { utc: '2026-07-15T11:30:00Z', zoneId: 'Europe/Madrid' },
    },
    duration: 90,
    carrier: { code: 'VY', name: 'Vueling' },
  };

  const merged = { ...base, ...overrides };

  const segments = overrides?.segments ?? [
    {
      departure: merged.departure,
      arrival: merged.arrival,
      duration: merged.duration,
      serviceNumber: 'VY1004',
      carrier: merged.carrier,
    },
  ];

  const availability: Availability = {
    status: 'AVAILABLE',
    seatsRemaining: 5,
    ...overrides?.availability,
  };

  return {
    id: generateId('trip'),
    ...merged,
    availability,
    pricing: createMockPricing(),
    segments,
    stops: overrides?.stops ?? 0,
    connections: overrides?.connections ?? segments.length - 1,
  };
}

/**
 * Create mock trip search response
 *
 * @param overrides - Partial overrides for the trip search response
 * @returns Mock trip search response
 */
export function createMockTripSearchResponse(overrides?: Partial<TripSearchResponse>): TripSearchResponse {
  return {
    id: generateId('search'),
    outbound: [
      createMockTripResult({
        id: 'outbound-1',
        departure: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: '2026-07-15T08:00:00Z', zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: '2026-07-15T09:30:00Z', zoneId: 'Europe/Madrid' },
        },
      }),
      createMockTripResult({
        id: 'outbound-2-connecting',
        departure: CONNECTING_SEGMENTS.outbound[0].departure,
        arrival: CONNECTING_SEGMENTS.outbound[1].arrival,
        duration: CONNECTING_SEGMENTS.outbound[0].duration + FRA_LAYOVER_DURATION + CONNECTING_SEGMENTS.outbound[1].duration,
        segments: [...CONNECTING_SEGMENTS.outbound],
      }),
      createMockTripResult({
        id: 'outbound-3',
        departure: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: '2026-07-15T12:00:00Z', zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: '2026-07-15T13:30:00Z', zoneId: 'Europe/Madrid' },
        },
      }),
      createMockTripResult({
        id: 'outbound-4',
        departure: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: '2026-07-15T18:00:00Z', zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: '2026-07-15T19:30:00Z', zoneId: 'Europe/Madrid' },
        },
      }),
    ],
    inbound: [
      createMockTripResult({
        id: 'inbound-1',
        departure: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: '2026-07-22T09:00:00Z', zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: '2026-07-22T10:30:00Z', zoneId: 'Europe/Madrid' },
        },
      }),
      createMockTripResult({
        id: 'inbound-2-connecting',
        departure: INBOUND_2STOP_SEGMENTS[0].departure,
        arrival: INBOUND_2STOP_SEGMENTS[INBOUND_2STOP_LAST_INDEX].arrival,
        duration: INBOUND_2STOP_SEGMENTS.reduce((sum, seg) => sum + seg.duration, 0),
        segments: [...INBOUND_2STOP_SEGMENTS],
      }),
      createMockTripResult({
        id: 'inbound-3',
        departure: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: '2026-07-22T15:00:00Z', zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: '2026-07-22T16:30:00Z', zoneId: 'Europe/Madrid' },
        },
      }),
    ],
    ...overrides,
  };
}

// ============== City Groups for browse availability mock ============== //

interface CityGroup {
  origin: string;
  destination: string;
  originZone: string;
  destinationZone: string;
  carrier: { code: string; name: string };
  durationMinutes: number;
}

/**
 * Pool of city-pair groups used by the availability browse mock.
 * Only the first CITY_GROUP_COUNT entries are active; add more below to extend the pool.
 */
export const CITY_GROUPS: CityGroup[] = [
  { origin: 'MAD', destination: 'BCN', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Madrid',    carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 85  },
  { origin: 'MAD', destination: 'LHR', originZone: 'Europe/Madrid',    destinationZone: 'Europe/London',    carrier: { code: 'IB', name: 'Iberia' },    durationMinutes: 145 },
  { origin: 'MAD', destination: 'CDG', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Paris',     carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 135 },
  { origin: 'MAD', destination: 'FCO', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Rome',      carrier: { code: 'IB', name: 'Iberia' },    durationMinutes: 165 },
  { origin: 'MAD', destination: 'AMS', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Amsterdam', carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 160 },
  { origin: 'BCN', destination: 'LIS', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Lisbon',    carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 120 },
  { origin: 'BCN', destination: 'MXP', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Rome',      carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 100 },
  { origin: 'BCN', destination: 'ATH', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Athens',    carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 195 },
  { origin: 'MAD', destination: 'VLC', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Madrid',    carrier: { code: 'VY', name: 'Vueling' },   durationMinutes: 55  },
  { origin: 'MAD', destination: 'AGP', originZone: 'Europe/Madrid',    destinationZone: 'Europe/Madrid',    carrier: { code: 'IB', name: 'Iberia' },    durationMinutes: 70  },
].slice(0, CITY_GROUP_COUNT);

/** Adds `minutes` to an ISO-8601 UTC string and returns an updated UTC string. */
function addMinutesToIso(isoString: string, minutes: number): string {
  const dte = new Date(isoString);
  dte.setUTCMinutes(dte.getUTCMinutes() + minutes);
  return dte.toISOString().replace(/\.\d+Z$/, 'Z');
}

// ============== Anywhere destinations for browse availability mock ============== //

interface AnywhereDestination {
  code: string;
  /** Optional city code for multi-airport cities (e.g. LON for LHR/LGW). */
  cityCode?: string;
  zone: string;
  durationMinutes: number;
  carrier: { code: string; name: string };
}

/** Spreads `count` departure times evenly between 06:00 and 21:00 UTC. */
function generateDepartureTimes(count: number): string[] {
  if (count === 1) return ['12:00:00Z'];
  const startMin = 6 * 60;
  const endMin = 21 * 60;
  const interval = (endMin - startMin) / (count - 1);
  return Array.from({ length: count }, (_, i) => {
    const total = Math.round(startMin + i * interval);
    const hh = Math.floor(total / 60).toString()
.padStart(2, '0');
    const mm = (total % 60).toString().padStart(2, '0');
    return `${hh}:${mm}:00Z`;
  });
}

/**
 * Airport pool for ANYWHERE browse mock. Ordered so same-city pairs are adjacent —
 * with ANYWHERE_TRIPS_PER_DATE=8 the first date always shows:
 *   BCN, LHR(LON), LGW(LON), CDG(PAR), ORY(PAR), FCO(ROM), CIA(ROM), AMS
 * giving 3 same-city pairs in one view. The second date shows the next 8, etc.
 */
const ANYWHERE_DESTINATIONS: AnywhereDestination[] = [
  { code: 'BCN',                  zone: 'Europe/Madrid',    durationMinutes: 85,  carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'LHR', cityCode: 'LON', zone: 'Europe/London',    durationMinutes: 145, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'LGW', cityCode: 'LON', zone: 'Europe/London',    durationMinutes: 140, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'CDG', cityCode: 'PAR', zone: 'Europe/Paris',     durationMinutes: 135, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'ORY', cityCode: 'PAR', zone: 'Europe/Paris',     durationMinutes: 130, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'FCO', cityCode: 'ROM', zone: 'Europe/Rome',      durationMinutes: 165, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'CIA', cityCode: 'ROM', zone: 'Europe/Rome',      durationMinutes: 160, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'AMS',                  zone: 'Europe/Amsterdam', durationMinutes: 160, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'LIS',                  zone: 'Europe/Lisbon',    durationMinutes: 120, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'ATH',                  zone: 'Europe/Athens',    durationMinutes: 195, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'VIE',                  zone: 'Europe/Vienna',    durationMinutes: 175, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'DUB',                  zone: 'Europe/Dublin',    durationMinutes: 155, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'PRG',                  zone: 'Europe/Prague',    durationMinutes: 180, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'FRA',                  zone: 'Europe/Berlin',    durationMinutes: 150, carrier: { code: 'IB', name: 'Iberia' }   },
  { code: 'MXP', cityCode: 'MIL', zone: 'Europe/Rome',      durationMinutes: 100, carrier: { code: 'VY', name: 'Vueling' }  },
  { code: 'LIN', cityCode: 'MIL', zone: 'Europe/Rome',      durationMinutes: 95,  carrier: { code: 'VY', name: 'Vueling' }  },
];

// ============================================================== //

const DEPARTURE_TIMES = ['07:00:00Z', '12:30:00Z', '17:45:00Z'];

function mockAvailability(seed: number): Availability {
  return seed % 5 === 0
    ? { status: 'LIMITED', seatsRemaining: 2 }
    : { status: 'AVAILABLE', seatsRemaining: 9 };
}

/**
 * Create mock availability browse response (GET) grouped by date over a range.
 * Generates 3 flights per date (morning, midday, afternoon) for every date in the range,
 * giving at least 15 trip results for the default 7-day browse window.
 *
 * When destination is "ANYWHERE", each journey goes to a different real airport from
 * ANYWHERE_DESTINATIONS; same-city pairs (e.g. LHR/LGW for London) naturally appear
 * together on the same date due to the cycling order.
 *
 * @param origin - Origin station code
 * @param destination - Destination station code, or "ANYWHERE"
 * @param dates - Ordered list of ISO dates (YYYY-MM-DD) to include in the response
 * @returns Mock availability browse response
 */
export function createMockAvailabilityBrowseResponse(origin: string, destination: string, dates: string[]): AvailabilitySearchResponse {
  const originZone = CITY_GROUPS.find(grp => grp.origin === origin)?.originZone ?? 'Europe/Madrid';

  if (destination === ANYWHERE_GROUP_CODE) {
    const anywhereDepTimes = generateDepartureTimes(ANYWHERE_TRIPS_PER_DATE);
    return {
      origin: { code: origin, type: 'AIRPORT' },
      destination: { code: destination, type: 'AIRPORT' },
      availabilityByDate: dates.map((date, dateIndex) => {
        const seed = date.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), dateIndex);
        const availability = mockAvailability(seed);

        return {
          date,
          journeys: anywhereDepTimes.map((time, tripIndex) => {
            const dest = ANYWHERE_DESTINATIONS[(dateIndex * ANYWHERE_TRIPS_PER_DATE + tripIndex) % ANYWHERE_DESTINATIONS.length]!;
            return {
              ...createMockTripResult({
                id: `browse-anywhere-${date}-${tripIndex + 1}`,
                departure: {
                  station: { code: origin, type: 'AIRPORT' },
                  date: { utc: `${date}T${time}`, zoneId: originZone },
                },
                arrival: {
                  station: { code: dest.code, type: 'AIRPORT', cityCode: dest.cityCode },
                  date: { utc: addMinutesToIso(`${date}T${time}`, dest.durationMinutes), zoneId: dest.zone },
                },
                duration: dest.durationMinutes,
                carrier: dest.carrier,
              }),
              availability,
            };
          }),
        };
      }),
    };
  }

  const group: CityGroup = CITY_GROUPS.find(grp => grp.origin === origin && grp.destination === destination)
    ?? { origin, destination, originZone, destinationZone: 'Europe/Madrid', carrier: { code: 'VY', name: 'Vueling' }, durationMinutes: 90 };

  return {
    origin: { code: origin, type: 'AIRPORT' },
    destination: { code: destination, type: 'AIRPORT' },
    availabilityByDate: dates.map((date, dateIndex) => {
      const seed = date.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), dateIndex);
      const availability = mockAvailability(seed);

      return {
        date,
        journeys: DEPARTURE_TIMES.map((time, tripIndex) => ({
          ...createMockTripResult({
            id: `browse-${date}-${tripIndex + 1}`,
            departure: {
              station: { code: origin, type: 'AIRPORT' },
              date: { utc: `${date}T${time}`, zoneId: group.originZone },
            },
            arrival: {
              station: { code: destination, type: 'AIRPORT' },
              date: { utc: addMinutesToIso(`${date}T${time}`, group.durationMinutes), zoneId: group.destinationZone },
            },
            duration: group.durationMinutes,
            carrier: group.carrier,
          }),
          availability,
        })),
      };
    }),
  };
}

/**
 * Create mock trip search parameters response
 *
 * @param overrides - Partial overrides for the trip search parameters response
 * @returns Mock trip search parameters response
 */
export function createMockTripSearchParameters(overrides?: Partial<TripSearchParametersResponse>): TripSearchParametersResponse {
  return {
    routes: [
      {
        origin: 'MAD',
        destinations: [
          {
            destination: 'BCN',
            tags: ['popular', 'domestic'],
            bookingWindow: {
              startDate: '2026-01-01T00:00:00.000Z',
              endDate: '2026-12-31T00:00:00.000Z',
            },
          },
          {
            destination: 'VLC',
            tags: ['domestic'],
            bookingWindow: {
              startDate: '2026-01-01T00:00:00.000Z',
              endDate: '2026-12-31T00:00:00.000Z',
            },
          },
          {
            destination: 'AGP',
            tags: ['popular', 'domestic'],
            bookingWindow: {
              startDate: '2026-01-01T00:00:00.000Z',
              endDate: '2026-12-31T00:00:00.000Z',
            },
          },
        ],
      },
    ],
    ...overrides,
  };
}

/**
 * Create mock availability parameters grouped by station groups (MAC and custom groups),
 * including GROUP-type destinations with their own booking window. Unlike
 * createMockTripSearchParameters, the response is a flat array (not wrapped in `routes`).
 *
 * @param overrides - Full replacement for the availability groups response
 * @returns Mock availability groups response
 */
export function createMockAvailabilityGroups(overrides?: AvailabilityGroupsResponse): AvailabilityGroupsResponse {
  return overrides ?? [
    {
      origin: 'MAD',
      type: 'AIRPORT',
      destinations: [
        {
          destination: 'BCN',
          type: 'AIRPORT',
          tags: ['popular', 'domestic'],
          bookingWindow: {
            startDate: '2026-01-01T00:00:00.000Z',
            endDate: '2026-12-31T00:00:00.000Z',
          },
        },
        {
          destination: 'VLC',
          type: 'AIRPORT',
          tags: ['domestic'],
          bookingWindow: {
            startDate: '2026-01-01T00:00:00.000Z',
            endDate: '2026-12-31T00:00:00.000Z',
          },
        },
        {
          destination: 'BEACH',
          type: 'STATION_GROUP',
          groupType: 'CUSTOM_GROUP',
          tags: ['popular'],
          bookingWindow: {
            startDate: '2026-02-01T00:00:00.000Z',
            endDate: '2026-10-31T00:00:00.000Z',
          },
        },
      ],
    },
  ];
}

/**
 * Create mock quotation response
 *
 * @param overrides - Partial overrides for the quotation response
 * @returns Mock quotation response
 */
export function createMockQuotationResponse(overrides?: Partial<QuotationResponse>): QuotationResponse {
  return {
    id: generateId('quotation'),
    pricing: createMockPricing(),
    ancillaries: [],
    passengers: [],
    ...overrides,
  };
}

const DEFAULT_PAGE_SIZE = 10;
const TOTAL_MOCK_BOOKINGS = 32;
const DAYS_BETWEEN_BOOKINGS = 7;
const DAYS_BETWEEN_PAGES = 30;

/**
 * Create mock booking list response with pagination support.
 * Later pages contain bookings with older departure dates.
 *
 * @param options - Pagination options (page, size)
 * @returns Mock booking list
 */
export function createMockBookingsListResponse(options: PaginationQueryParams = {}): BookingsListResponse {
  const { page = 0, size = DEFAULT_PAGE_SIZE } = options;
  const totalPages = Math.ceil(TOTAL_MOCK_BOOKINGS / size);
  const pageOffset = page * DAYS_BETWEEN_PAGES;

  const statuses: BookingStatus[] = ['CONFIRMED', 'COMPLETED', 'CANCELLED'];
  const content = Array.from({ length: size }, (_, i) => {
    const status = statuses[i % statuses.length]!;
    const dayOffset = -(pageOffset + i * DAYS_BETWEEN_BOOKINGS);

    return createMockBookingResponse({
      bookingStatus: status,
      reference: generateReference('BKG'),
      outbound: createMockTrip({
        departure: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: generateDateWithDayOffset(dayOffset), zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: generateDateWithDayOffset(dayOffset), zoneId: 'Europe/Madrid' },
        },
      }),
      inbound: createMockTrip({
        departure: {
          station: { code: 'BCN', type: 'AIRPORT' },
          date: { utc: generateDateWithDayOffset(dayOffset + DAYS_BETWEEN_BOOKINGS), zoneId: 'Europe/Madrid' },
        },
        arrival: {
          station: { code: 'MAD', type: 'AIRPORT' },
          date: { utc: generateDateWithDayOffset(dayOffset + DAYS_BETWEEN_BOOKINGS), zoneId: 'Europe/Madrid' },
        },
      }),
    });
  });

  return {
    content,
    page: {
      size,
      number: page,
      totalElements: TOTAL_MOCK_BOOKINGS,
      totalPages,
    },
    quota: {
      consumed: 3,
      availableBefore: 10,
      availableAfter: 7,
    },
  };
}

/**
 * Create mock booking response
 *
 * @param overrides - Partial overrides for the booking response
 * @returns Mock booking response
 */
export function createMockBookingResponse(overrides?: Partial<BookingResponse>): BookingResponse {
  // TODO: Do we have constants mamped for booking statuses?
  const bookingStatus: BookingStatus = overrides?.bookingStatus || 'CONFIRMED';
  const paymentStatus: PaymentStatus = overrides?.paymentStatus || 'PAID';
  const minutesOffset = 15;
  return {
    // INFO: use id: generateId('booking'), for random bookingId
    id: generateId('booking'),
    createdAt: generateDate(),
    expirationDate: generateDate(minutesOffset), // 15 minutes from now
    reference: generateReference('BKG'),
    bookingStatus,
    paymentStatus,
    passengers: [
      {
        person: {
          id: 'person-1',
          firstName: 'John',
          lastName: 'Doe',
          email: 'john.doe@example.com',
          dateOfBirth: '1990-01-15T00:00:00.000Z',
          gender: 'MALE',
          nationality: 'ES',
          currency: 'EUR',
          locale: 'en-US',
          phones: [
            {
              prefix: '+34',
              number: '600123456',
              type: 'MOBILE',
            },
          ],
          addresses: [
            {
              type: 'HOME',
              street: 'Calle Mayor 123',
              city: 'Madrid',
              state: 'Madrid',
              postalCode: '28001',
              country: 'ES',
            },
          ],
        },
        passengerType: 'ADULT' as PassengerType,
      },
    ],
    outbound: createMockTrip({
      departure: {
        station: { code: 'MAD', type: 'AIRPORT' },
        date: { utc: '2026-07-15T10:00:00Z', zoneId: 'Europe/Madrid' },
      },
      arrival: {
        station: { code: 'BCN', type: 'AIRPORT' },
        date: { utc: '2026-07-15T11:30:00Z', zoneId: 'Europe/Madrid' },
      },
    }),
    inbound: createMockTrip({
      departure: {
        station: { code: 'BCN', type: 'AIRPORT' },
        date: { utc: '2026-07-22T15:00:00Z', zoneId: 'Europe/Madrid' },
      },
      arrival: {
        station: { code: 'MAD', type: 'AIRPORT' },
        date: { utc: '2026-07-22T16:30:00Z', zoneId: 'Europe/Madrid' },
      },
    }),
    pricing: createMockPricingWithCredits(),
    selectedAncillaries: createMockSelectedAncillaries('person-1'),
    ancillaries: [],
    ...overrides,
  };
}

/**
 * Create mock selected ancillaries matching the pricing ancillaries.
 * Uses the shared ancillary sets to stay in sync with createMockPricing.
 *
 * @param personId - The person ID to associate ancillaries with
 * @returns Mock selected ancillaries per passenger
 */
export function createMockSelectedAncillaries(personId: string): SelectedAncillaryPassenger[] {
  return [
    {
      personId,
      outbound: toSelectedAncillaries(MOCK_ANCILLARIES_INCLUDED),
      inbound: toSelectedAncillaries(MOCK_ANCILLARIES_PURCHASED),
    },
  ];
}

export function createMockPassenger(overrides?: Partial<PassengerResponse>): PassengerResponse {
  return {
    person: {
      id: generateId('person'),
      firstName: 'Jane',
      lastName: 'Doe',
      email: 'jane.doe@example.com',
      currency: 'EUR',
      locale: 'en-US',
    },
    passengerType: 'ADULT',
    ...overrides,
  };
}

/** Mocked credits per passenger: one-way costs half a round trip. */
const MOCK_FLIGHT_OPTIONS: FlightOption[] = [
  { tripType: 'OW', credits: 1 },
  { tripType: 'RT', credits: 2 },
];

export function createMockPassengersResponse(overrides?: PassengersListResponse): PassengersListResponse {
  return overrides ?? [
    createMockPassenger({
      person: { id: 'person-subscriber', firstName: 'John', lastName: 'Smith', email: 'john.smith@example.com', currency: 'EUR', locale: 'en-US' },
      passengerType: 'ADULT',
      flightOptions: MOCK_FLIGHT_OPTIONS,
    }),
    createMockPassenger({
      person: { id: 'person-001', firstName: 'Jane', lastName: 'Doe', email: 'jane.doe@example.com', currency: 'EUR', locale: 'en-US' },
      passengerType: 'ADULT',
      flightOptions: MOCK_FLIGHT_OPTIONS,
    }),
    createMockPassenger({
      person: { id: 'person-002', firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com', currency: 'EUR', locale: 'en-US' },
      passengerType: 'CHILD',
      flightOptions: MOCK_FLIGHT_OPTIONS,
    }),
  ];
}

/** Mocked subscription-wide passenger cap, surfaced through the evaluation endpoint. */
const MOCK_MAX_PASSENGERS = 4;
const MOCK_CREDITS_PER_PASSENGER = 2;
const MOCK_AVAILABLE_CREDITS = 8;

/**
 * Mirrors the backend's adult-required rule so the local UI can be exercised end to end:
 * selecting the mocked CHILD without an adult returns an invalid composition.
 */
export function createMockPassengerEvaluationResponse(personIds: string[] = [], overrides?: Partial<PassengerEvaluationResponse>): PassengerEvaluationResponse {
  const pool = createMockPassengersResponse();
  const selected = pool.filter(passenger => personIds.includes(passenger.person.id));
  const isAdultMissing = selected.length > 0 && !selected.some(passenger => passenger.passengerType === 'ADULT' || passenger.passengerType === 'SENIOR');

  return {
    isValid: !isAdultMissing,
    messages: isAdultMissing
      ? [{ severity: 'error', code: 'PASSENGER-COMPOSITION-ADULT-REQUIRED', detail: 'Children and infants cannot travel without an adult' }]
      : [],
    limits: { maxPassengers: MOCK_MAX_PASSENGERS },
    quota: { required: selected.length * MOCK_CREDITS_PER_PASSENGER, available: MOCK_AVAILABLE_CREDITS, unlimited: false, sufficient: selected.length * MOCK_CREDITS_PER_PASSENGER <= MOCK_AVAILABLE_CREDITS },
    candidates: pool.map(passenger => ({ personId: passenger.person.id, selectable: true })),
    ...overrides,
  };
}
